import {
  ChangeDetectionStrategy,
  Component,
  inject,
  WritableSignal,
  signal,
  OnChanges,
  DOCUMENT,
  input,
} from '@angular/core';

import { AssignmentService, AssignmentType, DocumentDto } from '../../shared/generated/backend';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '../../shared/shared.module';
import { LoaderComponent } from '../../loader/loader.component';
import { DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { DxTabPanelModule, DxSelectBoxModule, DxPopupModule } from 'devextreme-angular';
import { DxScrollViewModule } from 'devextreme-angular';
import { SafePipe } from '../../shared/pipes/safe.pipe';
import notify from 'devextreme/ui/notify';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-assignment-documents',
  imports: [
    SharedModule,
    LoaderComponent,
    DxTabPanelModule,
    DxSelectBoxModule,
    DxDataGridModule,
    DxPopupModule,
    DxScrollViewModule,
    SafePipe,
  ],
  templateUrl: './assignment-documents.component.html',
  styleUrls: ['./assignment-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignmentDocumentsComponent implements OnChanges {
  readonly assignmentId = input.required<number>();
  readonly type = input.required<AssignmentType>();

  documents: WritableSignal<DocumentDto[]> = signal([]);
  selectedDocument: WritableSignal<DocumentDto | null> = signal(null);
  readonly isLoading: WritableSignal<boolean> = signal(false);
  currentBlobUrl: string | null = null;
  windowRef = window;

  private assignmentService = inject(AssignmentService);
  document = inject(DOCUMENT);
  private translateService = inject(TranslateService);

  async ngOnChanges() {
    this.isLoading.set(true);
    try {
      const dto = await firstValueFrom(this.assignmentService.getDocuments(this.assignmentId(), this.type()));
      this.documents.set(dto);
    } finally {
      this.isLoading.set(false);
    }
  }

  async previewDocument(doc: DocumentDto, event: Event): Promise<void> {
    event.stopPropagation();
    this.selectDocument(doc);

    if (!doc.Id) {
      return;
    }

    try {
      this.isLoading.set(true);
      const blob = await firstValueFrom(
        this.assignmentService.downloadDocument(doc.Id, this.assignmentId(), this.type())
      );
      const mimeType = this.getMimeType(doc.FileName);
      const fileBlob = new Blob([blob], { type: mimeType });
      const url = window.URL.createObjectURL(fileBlob);
      this.currentBlobUrl = url;
    } catch {
      notify({ message: this.translateService.instant('DOCU.DTO.PREVIEW_NOT_FOUND') }, 'error', 5_000);
    } finally {
      this.isLoading.set(false);
    }
  }

  selectDocument(doc: DocumentDto) {
    this.selectedDocument.set(doc);
  }

  getFileExtension(fileName?: string): string {
    if (!fileName) return '';
    const parts = fileName.split('.');
    return parts.length > 1 ? (parts.pop()?.toLowerCase() ?? '') : '';
  }

  private getMimeType(fileName: string | undefined): string {
    if (!fileName) return 'application/octet-stream';
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'bmp':
        return 'image/bmp';
      case 'msg':
        return 'application/vnd.ms-outlook';
      default:
        return 'application/octet-stream';
    }
  }

  async downloadDocument(doc: DocumentDto, event: Event): Promise<void> {
    event.stopPropagation();

    if (!doc.Id) {
      // Ensure the document has an ID
      console.error('Document ID is missing.');
      alert('Invalid document selection.');
      return;
    }

    try {
      this.isLoading.set(true);

      const blob = await firstValueFrom(
        this.assignmentService.downloadDocument(doc.Id!, this.assignmentId(), this.type())
      );
      if (blob === null) {
        // Ensure the document has an ID
        notify({ message: this.translateService.instant('DOCU.DTO.DOWNLOAD_NOT_FOUND') }, 'error', 5_000);
        return;
      }

      const mimeType = this.getMimeType(doc.FileName);

      const fileBlob = new Blob([blob], { type: mimeType });

      const url = window.URL.createObjectURL(fileBlob);
      const link = this.document.createElement('a');
      link.href = url;
      const fileExtension = this.getFileExtensionFromMimeType(mimeType);
      link.download = doc.FileName ?? `file.${fileExtension}`;

      this.document.body.appendChild(link);

      link.click();
      this.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      //@typescript-eslint/no-explicit-any
      notify({ message: this.translateService.instant('DOCU.DTO.DOWNLOAD_NOT_FOUND') }, 'error', 5_000);
    } finally {
      this.isLoading.set(false);
    }
  }

  private getFileExtensionFromMimeType(mimeType: string): string {
    const mimeTypeMapping: { [key: string]: string } = {
      'application/pdf': 'pdf',
      'text/plain': 'txt',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    };

    return mimeTypeMapping[mimeType] || 'bin'; // Default to 'bin' if MIME type is unknown
  }

  closePreview() {
    this.selectedDocument.set(null);

    if (this.currentBlobUrl) {
      window.URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
  }

  getFullPath(doc: DocumentDto | null): string {
    if (!doc) return '';
    const fileName = doc.FileName ?? 'document.pdf';
    const filePath = doc.Path ?? '';
    return `${filePath}${filePath.endsWith('/') ? '' : '/'}${fileName}`;
  }

  isPdf(fileName?: string): boolean {
    return fileName?.toLowerCase().endsWith('.pdf') ?? false;
  }

  isMsg(fileName?: string): boolean {
    return fileName?.toLowerCase().endsWith('.msg') ?? false;
  }

  isImage(fileName?: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp'];
    if (!fileName) return false;
    const lowerFileName = fileName.toLowerCase();
    return imageExtensions.some(ext => lowerFileName.endsWith(ext));
  }
}
